import { ImageResource } from '@tencent/alloy-renderer';
import {
    IImageCacheParams,
} from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-graphic/fill-style/image-cache/types';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { AUDIO_DEFAULT_IMG, VIDEO_DEFAULT_IMG } from '@src/slide/domain/ui-model/element-modeles/property/attachment/constants';
import ImageUrl from '@src/slide/infrastructure/module/dependent/image/image-toolkit/image-url';
import { CUSTOM } from '@src/slide/infrastructure/module/dependent/config/tob/constant';
import i18n from '@i18next/i18n';
import { FAIL_ICON_URL, generateFailImageUrl } from './fail-image';
import { isAudioElement, isIframeShapeModel, isVideoElement } from '@src/slide/domain/ui-model/elements/element-judge';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { IShapeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/shape-model.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import { imageCache } from '@tencent/alloy-graphics';
import { tdw } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/tdw-report';
import {
    TDWActionType,
    TDWModuleType,
    TDWOperationType,
} from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import { getCacConfigByKey } from '@src/slide/infrastructure/module/dependent/config/tob/app-config';

// 图片缓存
class ImageCache {
    // 加载失败托底图
    private defaultFailImage = new Image();
    // 缓存key对应的删除回调
    private keyDeleteCallBackMap = new Map();

    public constructor() {
        // 初始化失败兜底图
        this.defaultFailImage.src = FAIL_ICON_URL;
        imageCache.updateFailImage(FAIL_ICON_URL);
        imageCache.setSVGLoadedCallback(this.svgLoadedCallback);
    }

    // 通过ImageCache中转alloy-render的image获取，同一张图片直接读取缓存
    public get = (params: IImageCacheParams) => {
        const { customParams } = params;
        const isChart = !!customParams?.nonStandardParams?.chartRelation;

        // TODO keep 上线后移除
        if (isChart) {
            return new ImageResource(new Promise((resolve) => {
                const { width = 500, height } = customParams?.svgGraphics ?? {};
                const img = new Image();
                img.onload = () => resolve(img);
                img.src = generateFailImageUrl(i18n.t('暂不支持图表格式的展示和编辑'), width, height);
            }));
        }
        return imageCache.get(params);
    };

    // 根据入参获取当前存储的texture
    public getTexture = (params: IImageCacheParams) => imageCache.getTexture(params);

    // 确保当前model tree的所有图片均加载完成
    public promiseModelTreeImgLoaded = async (model, deepFind = true): Promise<void[]> => {
        if (!imageCache.requestListInfo.isLoading) {
            return Promise.all([]);
        }
        // 获取当前model tree保护的所有图片src
        const imgList: Array<string> = [];
        this.storeChildrenImg([model], imgList, deepFind);
        // 获取当前请求中的所有图片 promise
        const promiseArr: Array<Promise<void>> = [];
        const requestMapKeys = Array.from(imageCache.requestListInfo.requestMap.keys()).filter(e => typeof e === 'string') as string[];
        imgList.forEach((imgSrc) => {
            // 如果当前请求map中匹配的到，则说明此图片还未加载完成，如果没有则说明没有此图片或者已渲染完成
            const hitKey = requestMapKeys.find(e => e.indexOf(imgSrc) > -1);
            if (hitKey) {
                const promise = imageCache.requestListInfo.requestMap.get(hitKey);
                if (promise) {
                    promiseArr.push(promise.catch((err) => {
                        window.log.error('image-cache requestMap  error', err);
                    }));
                }
            }
        });
        // 返回Promise.all，外部可据此拿到所有图片加载完成时机
        return Promise.all(promiseArr);
    };

    // 限制图片的分辨率
    // 文档：(https://iwiki.woa.com/pages/viewpage.action?pageId=17501323，cos自定义样式 = imageView2/0/w/1600|imageView2/q/)
    // eslint-disable-next-line complexity
    public limitImageQuality = (url: string): string => {
        if (ImageUrl.isBlobUrl(url)) {
            return url;
        }

        const { width: picWidth, height: picHeight } = ImageUrl.getImageSizeFromUrl(url);
        // 无宽高设置
        if (isNaN(picWidth) || isNaN(picHeight)) {
            return url;
        }

        const cosImageRule = this.getThumbnailCosImageRule(url, { width: picWidth, height: picHeight });
        if (!cosImageRule) {
            return url;
        }
        const [origin, search] = url.split('?');
        // 已加上降低分辨率的处理
        if (search?.includes(cosImageRule)) {
            return url;
        }
        // 注意此处 qqadapt.qpic.cn 域名虽然有特殊处理，但是是历史逻辑，不清楚什么时候会用到
        const qqadaptHost = CUSTOM.CDN.QPIC;
        if (url.indexOf(qqadaptHost) > 0) {
            const cosImageRuleOld = '1600';
            return `${url.substring(0, origin.length - 1)}${cosImageRuleOld}?${search}`;
        }
        // 降低分辨率的处理不再使用 /1600，这个是历史映射，在不同端的环境下没有通用性(例如B端的图片复制到C端)
        return `${origin}?${cosImageRule}&${search}`;
    };

    /**
     * 获取cos缩略图策略，使用原图时返回undefined
     * 原实现为imageView2/2/w/1600/h/900，因效果不好改为quality(imageView2/q，只对jpeg和webp格式有效，其他格式改为imageView2/0/w/1600)。
     * 试验数据：
     * jpeg：9500 * 7084，20.2M
     * 环境：mbp, 分辨率1728 * 1117, devicePixelRatio 2
     * |w/1600/h/900|w/3200/h/1800|quality20|quality50|
     * |383k|1.5M|2.2M|4.4M|
     * |很渣|渣|不错|很不错|
     * imageMogr2/ignore-error/1 是高级图片处理，用于在图片转换错误的时候返回原图
     * */
    // eslint-disable-next-line complexity
    public getThumbnailCosImageRule = (url: string, imageSize: { width: number, height: number }) => {
        const { width, height } = window.screen;
        const curScreenWidth = width * window.devicePixelRatio;
        const curScreenHeight = height * window.devicePixelRatio;
        const { width: picWidth, height: picHeight } = imageSize;
        const isJpgOrWebp = /\.(jpeg|jpg|webp)(\?|$)/i.test(url);
        if (isJpgOrWebp) {
            const ratio = picWidth * picHeight / (curScreenWidth * curScreenHeight);
            if (ratio <= 1) return undefined;
            let quality;
            if (ratio > 1 && ratio <= 2) {
                quality = 80;
            } else if (ratio > 2 && ratio <= 4) {
                quality = 50;
            } else {
                quality = 20;
            }
            if (getCacConfigByKey('custom.common.onlyImageMogr2', false)) {
                if (getCacConfigByKey('custom.imageThumbnail', false)) {
                    return `imageMogr2/thumbnail/!${quality}p/ignore-error/1`;
                }
                return undefined;
            }
            return `imageView2/q/${quality}|imageMogr2/ignore-error/1`;
        }
        if (picWidth * picHeight > curScreenWidth * curScreenHeight) {
            if (getCacConfigByKey('custom.common.onlyImageMogr2', false)) {
                if (getCacConfigByKey('custom.imageThumbnail', false)) {
                    return 'imageMogr2/thumbnail/1600x/ignore-error/1';
                }
                return undefined;
            }
            return 'imageView2/0/w/1600|imageMogr2/ignore-error/1';
        }
        return undefined;
    };

    // lru缓存失效回调，在此回调中执行调用方传入的监听
    private runCacheLoseCallBack = (losePoolKey) => {
        if (this.keyDeleteCallBackMap.has(losePoolKey)) {
            // 遍历所有get时传入的删除回调并执行
            this.keyDeleteCallBackMap.get(losePoolKey).forEach((cacheLoseCallback) => {
                if (typeof cacheLoseCallback === 'function') {
                    cacheLoseCallback({ urlOrImage: losePoolKey });
                }
            });
            this.keyDeleteCallBackMap.delete(losePoolKey);
        }
    };

    // 遍历所有层级的子元素，将包含的图片src存入imgList
    private storeChildrenImg = (children, imgList, deepFind = true) => {
        children.forEach((item) => {
            const modelImgArr = this.getModelImgSrc(item);
            modelImgArr.forEach((imgSrc) => {
                imgList.push(imgSrc.split('?')[0]);
            });
            if (item.children && deepFind) {
                this.storeChildrenImg(item.children, imgList);
            }
            if (item.type === UIModelType.TABLE) {
                let tableItemArr = [];
                item.props?.items?.forEach((tableItems) => {
                    tableItemArr = tableItemArr.concat(tableItems);
                });
                this.storeChildrenImg(tableItemArr, imgList);
            }
        });
    };

    // 获取model的图片src
    // eslint-disable-next-line complexity
    private getModelImgSrc = (model: IElementBaseModel): Array<string> => {
        const imgArr: Set<string | undefined> = new Set();
        // 前景
        if (model?.type === UIModelType.IMAGE || model?.type === UIModelType.SHAPE) {
            let defaultSrc = '';
            // 视频封面截图
            if (isVideoElement(model)) {
                defaultSrc = VIDEO_DEFAULT_IMG;
            }
            // 音频占位图标
            if (isAudioElement(model)) {
                defaultSrc = AUDIO_DEFAULT_IMG;
            }
            const mediaManager = model.getMediaManager();
            if (mediaManager) {
                // iframe 截图
                if (isIframeShapeModel(model)) {
                    const iframePoster = mediaManager.iframePosterCaches.get(model.key);
                    if (iframePoster) defaultSrc = iframePoster;
                }
            }
            imgArr.add(defaultSrc);
        }
        if (model?.type === UIModelType.IMAGE) {
            const pictureModel = (model as IPictureModel);
            if (pictureModel.props?.src) {
                imgArr.add((model as IPictureModel)?.props?.src);
            }
            if (pictureModel?.props?.backupSrc) {
                imgArr.add(pictureModel?.props?.backupSrc);
            }
        }
        // 背景
        if (model?.props?.style?.fillBackground?.image?.src) {
            imgArr.add(model?.props?.style?.fillBackground?.image?.src);
        }
        // 文字图片填充
        if (model?.type === UIModelType.SHAPE) {
            (model as IShapeModel).paragraphList?.forEach((paragraph) => {
                paragraph.textRuns?.forEach((text) => {
                    imgArr.add(text.styles?.image?.src);
                });
                imgArr.add(paragraph.paragraphCharacter?.buBlip?.blip.src);
            });
        }
        return Array.from(imgArr).filter(e => !!e) as string[];
    };

    private svgLoadedCallback = (src: string, svgPath: string): void => {
        // 添加上报，统计存在 foreignObject 的 svg
        tdw({
            opername: TDWOperationType.DOC_PPT,
            module: TDWModuleType.CANVAS_RENDERER,
            action: TDWActionType.SVG_FOREIGN_OBJECT,
            ver6: JSON.stringify({
                href: window.location.href,
                svgSrc: src,
                svgPath,
            }),
        });
    };
}

export default new ImageCache();
